"use client";

// ============================================================
// CLUBHUB – Personen auswaehlen (fuer alle Module)
// ============================================================
// Suchen und waehlen von Mitgliedern/Kontakten: einzeln (Rechnung an …)
// oder mehrfach (Gruppe fuellen, Empfaenger). Sucht in Name,
// Mitgliedsnummer, Mail und Ort. In der Maskenphase ueber den
// Musterbestand; mit dem Backend ueber eine Suche auf dem Server — die
// Props bleiben.
//
//   <KontaktAuswahl value={id} onChange={setId} />
//   <KontaktAuswahl mehrfach values={ids} onChangeMany={setIds} />
// ============================================================
import { useMemo, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { cn, initials } from "@/lib/utils";
import { MOCK_CONTACTS } from "@/lib/mock/core";
import type { ContactView } from "@/lib/views";
import type { ContactStatus } from "@/generated/prisma/enums";
import { ContactStatusBadge } from "./status-badge";

type Gemeinsam = {
  /** Nur diese Status anbieten (Standard: alle). */
  status?: ContactStatus[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Quelle der Personen (Standard: Musterbestand). */
  kontakte?: ContactView[];
};

type Einzeln = Gemeinsam & { mehrfach?: false; value: string | null; onChange: (id: string | null) => void };
type Mehrfach = Gemeinsam & { mehrfach: true; values: string[]; onChangeMany: (ids: string[]) => void };

function treffer(c: ContactView, q: string) {
  if (!q) return true;
  const s = q.toLowerCase();
  return [c.displayName, c.memberNumber, c.email, c.city].some((f) => f?.toLowerCase().includes(s));
}

export function KontaktAuswahl(props: Einzeln | Mehrfach) {
  const quelle = props.kontakte ?? MOCK_CONTACTS;
  const [offen, setOffen] = useState(false);
  const [q, setQ] = useState("");
  const eingabe = useRef<HTMLInputElement>(null);

  const gewaehlt = props.mehrfach ? props.values : props.value ? [props.value] : [];
  const liste = useMemo(
    () =>
      quelle
        .filter((c) => !props.status || props.status.includes(c.status))
        .filter((c) => treffer(c, q))
        .slice(0, 50),
    [quelle, props.status, q],
  );
  const name = (id: string) => quelle.find((c) => c.id === id)?.displayName ?? id;

  function waehle(id: string) {
    if (props.mehrfach) {
      props.onChangeMany(gewaehlt.includes(id) ? gewaehlt.filter((x) => x !== id) : [...gewaehlt, id]);
    } else {
      props.onChange(id === props.value ? null : id);
      setOffen(false);
    }
  }

  return (
    <Popover.Root open={offen} onOpenChange={(o) => { setOffen(o); if (!o) setQ(""); }}>
      <Popover.Trigger asChild disabled={props.disabled}>
        <button
          type="button"
          className={cn(
            "border-border-control bg-bg-primary text-body-sm flex min-h-9 w-full items-center gap-2 rounded-md border px-3 py-1.5 text-left",
            "hover:border-border-strong duration-fast transition-colors disabled:opacity-50",
            props.className,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap gap-1">
            {gewaehlt.length === 0 && (
              <span className="text-text-tertiary">{props.placeholder ?? (props.mehrfach ? "Personen wählen …" : "Person wählen …")}</span>
            )}
            {!props.mehrfach && gewaehlt[0] && <span className="text-text-primary truncate">{name(gewaehlt[0])}</span>}
            {props.mehrfach &&
              gewaehlt.map((id) => (
                <span key={id} className="bg-bg-tertiary text-caption text-text-primary inline-flex items-center gap-1 rounded px-1.5 py-0.5">
                  {name(id)}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`${name(id)} entfernen`}
                    onClick={(e) => { e.stopPropagation(); waehle(id); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); waehle(id); } }}
                    className="text-text-tertiary hover:text-text-primary"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </span>
                </span>
              ))}
          </span>
          <ChevronsUpDown className="text-text-tertiary h-4 w-4 shrink-0" aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          onOpenAutoFocus={(e) => { e.preventDefault(); eingabe.current?.focus(); }}
          className="border-border bg-bg-primary shadow-dropdown animate-fade-in z-50 w-[min(420px,calc(100vw-24px))] overflow-hidden rounded-lg border"
        >
          <div className="border-border-subtle flex items-center gap-2 border-b px-3">
            <Search className="text-text-tertiary h-4 w-4" aria-hidden />
            <input
              ref={eingabe}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, Nummer, Mail, Ort …"
              className="text-body-sm placeholder:text-text-tertiary h-10 flex-1 bg-transparent outline-none"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto py-1" role="listbox" aria-multiselectable={props.mehrfach || undefined}>
            {liste.length === 0 && <li className="text-body-sm text-text-tertiary px-3 py-6 text-center">Niemand gefunden.</li>}
            {liste.map((c) => {
              const an = gewaehlt.includes(c.id);
              return (
                <li key={c.id} role="option" aria-selected={an}>
                  <button
                    type="button"
                    onClick={() => waehle(c.id)}
                    className="hover:bg-bg-secondary flex w-full items-center gap-3 px-3 py-2 text-left"
                  >
                    <span className="bg-bg-tertiary text-caption text-text-secondary grid h-7 w-7 shrink-0 place-items-center rounded-full font-medium">
                      {initials(c.displayName.replace(",", ""))}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="text-body-sm text-text-primary block truncate">{c.displayName}</span>
                      <span className="text-caption text-text-tertiary block truncate">
                        {c.memberNumber ? <span className="tabular-nums">Nr. {c.memberNumber} · </span> : null}
                        {c.city ?? c.email ?? ""}
                      </span>
                    </span>
                    <ContactStatusBadge status={c.status} />
                    <Check className={cn("text-accent h-4 w-4 shrink-0", an ? "opacity-100" : "opacity-0")} aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
